import { PointArray } from '@editor/interfaces/attribute'
import { createRayCaster, getClickCoords } from '@editor/services/Points'
import { RootState } from '@editor/store/app'
import { useSelector } from 'react-redux'
import { Camera, Intersection, Object3D, Object3DEventMap, Plane, Vector3 } from 'three'

import { useBlueprintEditorContext } from '../contexts/BlueprintEditorContext'
import { Mode } from '../definitions'
import useProjectionPlane from './useProjectionPlane'

const rayCaster = createRayCaster()

const intersectionResult = new Vector3()

const usePointSelection = () => {
  const { pointClouds, activeCamera, sceneRef } = useBlueprintEditorContext()
  const mode = useSelector((state: RootState) => state.blueprint.editor.mode)
  const { projectionPlane } = useProjectionPlane()

  /**
   * Get the intersection point of a mouse or touch event with the 3D scene.
   * @param mouseEvent The mouse event.
   * @param touchEvent The touch event.
   * @param targets The target objects and planes for intersection.
   * @returns The closest intersection point to the camera or undefined if not found.
   */
  const getIntersection = (
    mouseEvent: React.MouseEvent<HTMLDivElement> | { clientX: number; clientY: number } | null,
    touchEvent?: React.TouchEvent<HTMLDivElement>,
    targets?: {
      objects?: Object3D<Object3DEventMap>[]
      plane?: Plane
    },
  ): PointArray | undefined => {
    if (!activeCamera?.current || !sceneRef.current) return undefined

    //* カメラ取得
    const camera = activeCamera.current as Camera

    //* 光線生成とクリック位置特定
    const targetElem = document.getElementById('canvasBox')?.getBoundingClientRect()
    if (!targetElem) {
      return undefined
    }

    const { x, y, width, height } = targetElem
    const { coords } = getClickCoords(
      {
        x: mouseEvent?.clientX || touchEvent?.touches[0].clientX || 0,
        y: mouseEvent?.clientY || touchEvent?.touches[0].clientY || 0,
      },
      {
        x,
        y,
        width,
        height,
      },
    )

    //* カメラからpointまで光線を伸ばす
    rayCaster.setFromCamera(coords, camera)

    if (!pointClouds.length) return undefined

    rayCaster.params.Points.threshold = 0.02

    // If specific objects or planes are provided, use them for intersection
    if (targets?.plane) {
      const intersection = rayCaster.ray.intersectPlane(targets.plane, intersectionResult)
      if (!intersection) {
        return undefined
      }
      return intersectionResult.toArray()
    }

    // On Blueprint mode, use blueprint canvas for intersection
    if (mode === Mode.Editor2D && projectionPlane) {
      const intersection = rayCaster.ray.intersectPlane(projectionPlane, intersectionResult)
      if (!intersection) {
        return undefined
      }
      return intersectionResult.toArray()
    }

    // Lookd for solid mesh via its manager in the scene
    const solidMesh = sceneRef.current?.getObjectByName('solid-mesh-manager') || undefined

    // Object-based intersection
    const objects = targets?.objects || [...pointClouds, ...(solidMesh ? [solidMesh] : [])]
    let intersects: Intersection<Object3D<Object3DEventMap>>[] = []
    if (!intersects.length) {
      intersects = rayCaster.intersectObjects(objects, true)
    }

    const intersectedPoint = intersects.reduce<Intersection<Object3D<Object3DEventMap>> | undefined>(
      (prev, current) => {
        const { index, faceIndex, distance } = current
        if (index === undefined && faceIndex === undefined) return prev

        // If mask regions has been defined, filter out points that are not within them but only for PCD intersections.
        // Intersections with any other object is always allowed.
        // Note that 'maskModelMatrices' has already been filtered to only include visible masks.
        const result = true
        // if (object instanceof Points && !isViewAllPoints && maskModelMatrices.length) {
        //   result = maskModelMatrices.some((mask) => {
        //     const localPoint = point.clone().applyMatrix4(mask.modelMatrix)
        //     const box = new Box3(mask.min, mask.max)
        //     return box.containsPoint(localPoint)
        //   })
        // }

        if (result && (!prev || distance < prev.distance)) return current
        return prev
      },
      undefined,
    )

    if (!intersectedPoint) {
      return undefined
    }

    return intersectedPoint?.point.toArray()
  }

  return {
    getIntersection,
  }
}

export default usePointSelection
